import { ExportCostTypeItem } from "../enums";
import type { CostTypeMapper, DataPointCost, ExportCostByType } from "../types";

/**
 * Calculates the cost for a single data point (i.e. column in the table) using provided mapper
 */
export const getDataPointCost = <T extends string>(
    dataPoints: DataPointCost | null | undefined,
    dataPointType: T,
    mapper: CostTypeMapper<T>,
): number | null => {
    if (!dataPoints) {
        return null;
    }

    const costType = mapper(dataPointType);

    if (costType === null) {
        return 0;
    }

    return dataPoints[costType];
};

/**
 * Calculates the total cost for a collection of data points using the provided mapper
 */
export const calculateTotalDataPointsCost = <T extends string>(
    dataPoints: DataPointCost | null | undefined,
    dataPointTypesList: readonly T[],
    mapper: CostTypeMapper<T>,
): number | null => {
    if (!dataPoints) {
        return null;
    }

    return dataPointTypesList.reduce((sum, type) => sum + (getDataPointCost(dataPoints, type, mapper) ?? 0), 0);
};

export const getExportCostType = (
    exportType: ExportCostTypeItem,
    isCompanyAsLeadExport: boolean,
): ExportCostTypeItem => {
    if (exportType === ExportCostTypeItem.Salesforce) {
        return isCompanyAsLeadExport ? ExportCostTypeItem.SalesforceLead : ExportCostTypeItem.Salesforce;
    }

    if (exportType === ExportCostTypeItem.Dynamics) {
        return isCompanyAsLeadExport ? ExportCostTypeItem.DynamicsLead : ExportCostTypeItem.Dynamics;
    }

    return exportType;
};

export const getCrmExportPrice = (costByType: ExportCostByType | undefined, exportType: ExportCostTypeItem): number =>
    costByType?.[exportType as keyof ExportCostByType] ?? 0;

type FlatCostParams = {
    exportType: ExportCostTypeItem;
    costPerRecord: ExportCostByType | undefined;
    isCompanyAsLeadExport: boolean;
};

type ColumnCostParams<T extends string> = {
    columnTypes: readonly T[];
    costTypeMapper: CostTypeMapper<T>;
    dataPoints: DataPointCost | null | undefined;
};

export const calculateExportPricePerRecord = <T extends string>(
    params: FlatCostParams | ColumnCostParams<T>,
): number => {
    if ("exportType" in params) {
        return getCrmExportPrice(
            params.costPerRecord,
            getExportCostType(params.exportType, params.isCompanyAsLeadExport),
        );
    }

    return params.columnTypes.length > 0
        ? (calculateTotalDataPointsCost(params.dataPoints, params.columnTypes, params.costTypeMapper) ?? 0)
        : 0;
};
